/**
 * Lembrete diário do pedido de permissão de notificações (Web Push).
 *
 * Antes a dispensa era por sessão (`sessionStorage`), então cada nova aba
 * repetia o pedido. Agora o cliente é lembrado **uma vez por dia**: o registro
 * fica em `localStorage`, sobrevive a abas/recarregamentos e é por usuário —
 * trocar de conta no mesmo navegador não herda o "já vi hoje" da conta anterior.
 *
 * Quem já bloqueou as notificações no navegador (`permission === 'denied'`) só
 * é lembrado a cada `JANELA_LEMBRETE_BLOQUEADO` dias: insistir todos os dias em
 * um bloqueio que só o próprio usuário pode desfazer vira ruído.
 *
 * As datas são gravadas no fuso local (`YYYY-MM-DD`, não UTC): "a cada dia"
 * precisa bater com o dia do calendário do cliente.
 */

const PREFIXO_CHAVE = 'push-permission-prompt:';
const PREFIXO_LEGADO = 'push-permission-prompt-dismissed:';

/** Dias de espera entre lembretes quando o navegador já bloqueou o site. */
export const JANELA_LEMBRETE_BLOQUEADO = 7;

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/** Fallback em memória: `localStorage` pode estar bloqueado ou com cota cheia. */
const memoria = new Map();

export const chaveLembrete = (userId) => `${PREFIXO_CHAVE}${userId || 'anon'}`;

const chaveLegada = (userId) => `${PREFIXO_LEGADO}${userId || 'anon'}`;

const doisDigitos = (valor) => String(valor).padStart(2, '0');

/** Data local no formato `YYYY-MM-DD` (ordenável e comparável por igualdade). */
export const dataLocalISO = (data = new Date()) =>
  `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;

export const hojeISO = (agora = new Date()) => dataLocalISO(agora);

const paraData = (iso) => {
  const partes = String(iso || '').split('-');
  if (partes.length !== 3) return null;

  const [ano, mes, dia] = partes.map(Number);
  if (!ano || !mes || !dia) return null;

  const data = new Date(ano, mes - 1, dia);

  // Rejeita datas que "rolam" no calendário (ex.: 2026-13-01, 2026-02-30).
  const valida =
    !Number.isNaN(data.getTime()) &&
    data.getFullYear() === ano &&
    data.getMonth() === mes - 1 &&
    data.getDate() === dia;

  return valida ? data : null;
};

/** Diferença em dias entre duas datas locais (`null` se alguma for inválida). */
export const diasEntre = (isoAnterior, isoAtual) => {
  const anterior = paraData(isoAnterior);
  const atual = paraData(isoAtual);
  if (!anterior || !atual) return null;

  // As duas pontas estão à meia-noite local: o `round` absorve as 23h/25h de
  // horário de verão sem trocar o dia do calendário.
  return Math.round((atual.getTime() - anterior.getTime()) / MS_POR_DIA);
};

const lerBruto = (chave) => {
  try {
    const valor = localStorage.getItem(chave);
    if (valor !== null) return valor;
  } catch {
    // Storage bloqueado: cai para a memória.
  }

  return memoria.has(chave) ? memoria.get(chave) : null;
};

const gravarBruto = (chave, valor) => {
  try {
    localStorage.setItem(chave, valor);
    memoria.delete(chave);
  } catch {
    memoria.set(chave, valor);
  }
};

const removerBruto = (chave) => {
  memoria.delete(chave);

  try {
    localStorage.removeItem(chave);
  } catch {
    // Nada a fazer: o registro em memória já foi descartado.
  }
};

const marcadoNaSessao = (userId) => {
  try {
    return sessionStorage.getItem(chaveLegada(userId)) === '1';
  } catch {
    return false;
  }
};

const limparLegado = (userId) => {
  try {
    sessionStorage.removeItem(chaveLegada(userId));
  } catch {
    // Nada a fazer.
  }
};

/** Registro do lembrete do usuário: `{ ultimaExibicao: 'YYYY-MM-DD' | null }`. */
export const lerLembrete = (userId) => {
  const bruto = lerBruto(chaveLembrete(userId));

  if (!bruto) {
    // Migração: quem dispensou nesta sessão com a regra antiga não é
    // incomodado de novo hoje.
    return { ultimaExibicao: marcadoNaSessao(userId) ? hojeISO() : null };
  }

  try {
    const dados = JSON.parse(bruto);
    if (dados && typeof dados.ultimaExibicao === 'string' && paraData(dados.ultimaExibicao)) {
      return { ultimaExibicao: dados.ultimaExibicao };
    }
  } catch {
    // Registro corrompido: tratado como "nunca exibido".
  }

  return { ultimaExibicao: null };
};

/** Marca que o cliente foi lembrado hoje (chamado ao abrir o diálogo). */
export const marcarExibicao = (userId, agora = new Date()) => {
  gravarBruto(chaveLembrete(userId), JSON.stringify({ ultimaExibicao: hojeISO(agora) }));
  limparLegado(userId);
};

/** Esquece o lembrete — usado quando as notificações são ativadas. */
export const limparLembrete = (userId) => {
  removerBruto(chaveLembrete(userId));
  limparLegado(userId);
};

/**
 * `true` quando o cliente deve ser lembrado hoje.
 *
 * - nunca lembrado → sim;
 * - já lembrado hoje → não;
 * - data futura (relógio/fuso mudou) → sim, para não ficar travado;
 * - bloqueado no navegador → só depois de `JANELA_LEMBRETE_BLOQUEADO` dias.
 */
export const deveLembrarHoje = ({ userId, permissao = 'default', agora = new Date() } = {}) => {
  const { ultimaExibicao } = lerLembrete(userId);
  if (!ultimaExibicao) return true;

  const dias = diasEntre(ultimaExibicao, hojeISO(agora));
  if (dias === null) return true;
  if (dias < 0) return true;
  if (dias === 0) return false;

  return permissao === 'denied' ? dias >= JANELA_LEMBRETE_BLOQUEADO : true;
};

const pushPromptUtils = {
  JANELA_LEMBRETE_BLOQUEADO,
  chaveLembrete,
  dataLocalISO,
  hojeISO,
  diasEntre,
  lerLembrete,
  marcarExibicao,
  limparLembrete,
  deveLembrarHoje,
};

export default pushPromptUtils;
